import { useApi } from '@hero/context';
import type { MyAnalyticsOverviewAPI } from '@hero/api';
import type { UseMyAnalyticsOverviewApi } from './types';
import { useQueryAPI } from '../../utils';

/**
 * Role-neutral analytics overview hook. Wraps
 * `GET /analytics/my-overview` and its backend counterpart
 * `AnalyticsOverviewService.getMyOverview` — the single unified path
 * every consumer of the home / dashboard tiles goes through
 * (carrier-dashboard, customer-dashboard, carrier-app home,
 * AI order agent's `get_order_analytics_summary`).
 *
 * Every count on the response comes from the shared
 * `OrderQueryService` engine, so the numbers here match
 * `useMyOrdersListApi`, `useCountOrdersApi`, and the tile UI for
 * the same params.
 *
 * ## Lens
 * - Omit `lens` (or pass `'none'`) → tenant-wide view (every Order
 *   the caller is a tenant of), same numbers the carrier-app home
 *   and carrier-dashboard have always shipped.
 * - Pass `lens: 'as_customer' | 'as_shipper'` → narrow the DB query
 *   to orders where the caller is the buyer.
 * - Pass `lens: 'as_carrier' | 'as_driver'` → narrow to seller side.
 *
 * ## Shape
 * Returns `AnalyticsCarrierDashboardData` (the superset). Seller-only
 * blocks (`earnings`, `needsDriver`, `utilization`, `drivers`) are
 * populated with zeros for buyer lenses so the shape
 * stays uniform; the shared `<AnalyticsOverview>` component gates on
 * `displayForUser` and won't render seller blocks on customer dashboards.
 *
 * Hook API: {@link UseMyAnalyticsOverviewApi}
 */
export const useMyAnalyticsOverviewApi: UseMyAnalyticsOverviewApi = (query, apiConfig) => {
  const api = useApi();
  return useQueryAPI({
    entity: 'analyticsOverview',
    apiFn: api.analytics.getMyAnalyticsOverview,
    // The hook accepts a Partial<req>; cast to the full req at the
    // useQueryAPI boundary because every field on the request schema
    // has a server-side default that fills in the missing values.
    query: query as MyAnalyticsOverviewAPI['req'] | undefined,
    apiConfig,
  });
};
